import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Undo2, Loader2, Check, X } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tela do admin para decidir sobre solicitações de estorno - peça que
 * faltava, identificada na varredura: o sistema mencionava "abra um
 * processo de estorno" mas esse processo não existia de verdade.
 */
const EstornosAdmin = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [decidindoId, setDecidindoId] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/estornos/pendentes`);
      if (resp.ok) setSolicitacoes(await resp.json());
    } catch (error) {
      console.error("Erro ao carregar estornos:", error);
      toast.error("Não foi possível carregar as solicitações.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const decidir = async (estornoId, aprovar) => {
    const observacao = window.prompt(aprovar ? "Observação (opcional):" : "Motivo da negativa (opcional):");
    setDecidindoId(estornoId);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/estornos/${estornoId}/decidir`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aprovar, observacao: observacao || null }),
      });
      if (!resp.ok) throw new Error("Erro ao decidir");
      toast.success(aprovar ? "Estorno aprovado." : "Estorno negado.");
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDecidindoId(null);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Undo2 className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Solicitações de Estorno</h1>
          <p className="text-sm text-gray-400">Pedidos com repasse já autorizado, pedindo revisão</p>
        </div>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : solicitacoes.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          Nenhuma solicitação de estorno pendente.
        </Card>
      ) : (
        <div className="space-y-3">
          {solicitacoes.map((s) => (
            <Card key={s.id} className="p-4 bg-gray-800/50 border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-medium">Pedido {s.pedido_id.slice(0, 8)}...</p>
                <span className="text-emerald-400 font-semibold text-sm">R$ {(s.valor_pedido || 0).toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-400 mb-1">Solicitado por: {s.solicitado_por_email}</p>
              <p className="text-sm text-gray-300 mb-3">"{s.motivo}"</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => decidir(s.id, true)}
                  disabled={decidindoId === s.id}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Aprovar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => decidir(s.id, false)}
                  disabled={decidindoId === s.id}
                  className="border-red-800 text-red-400"
                >
                  <X className="w-3 h-3 mr-1" />
                  Negar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EstornosAdmin;
